import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Clock, Send, Calendar, Lock, Unlock, Heart, Star, Sparkles,
  MessageCircle, Video, Mic, Image as ImageIcon, Gift, Users,
  ChevronRight, ChevronLeft, Plus, X, Play, Pause, Eye, Bell,
  Timer, Hourglass, Rocket, Globe, Mail, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { FloatingBackButton } from '../components/layout/FloatingBackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Glassmorphism CSS keyframes
const glassKeyframes = `
  @keyframes holographicShift {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
  @keyframes floatSlow {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50% { transform: translateY(-10px) rotate(1deg); }
  }
  @keyframes pulseGlow {
    0%, 100% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.3), inset 0 0 20px rgba(99, 102, 241, 0.05); }
    50% { box-shadow: 0 0 40px rgba(99, 102, 241, 0.5), inset 0 0 30px rgba(99, 102, 241, 0.1); }
  }
  @keyframes shimmerGlass {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  @keyframes orbFloat {
    0%, 100% { transform: translate(0, 0) scale(1); }
    25% { transform: translate(10px, -10px) scale(1.05); }
    50% { transform: translate(0, -20px) scale(1); }
    75% { transform: translate(-10px, -10px) scale(0.95); }
  }
`;

// Glass Card Component
const GlassCard = ({ children, className = '', glow = false, hover = true, onClick }) => (
  <motion.div
    whileHover={hover ? { scale: 1.02, y: -2 } : {}}
    whileTap={onClick ? { scale: 0.98 } : {}}
    onClick={onClick}
    className={`relative overflow-hidden rounded-3xl backdrop-blur-xl ${className}`}
    style={{
      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      boxShadow: glow 
        ? '0 8px 32px rgba(99, 102, 241, 0.2), inset 0 0 32px rgba(99, 102, 241, 0.05)'
        : '0 8px 32px rgba(0, 0, 0, 0.2)',
    }}
  >
    {/* Holographic shimmer effect */}
    <div 
      className="absolute inset-0 opacity-30 pointer-events-none"
      style={{
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
        backgroundSize: '200% 100%',
        animation: 'shimmerGlass 3s ease-in-out infinite',
      }}
    />
    {children}
  </motion.div>
);

// Time Capsule Card
const TimeCapsuleCard = ({ capsule, onOpen }) => {
  const [timeLeft, setTimeLeft] = useState('');
  const isLocked = new Date(capsule.unlock_date) > new Date();
  
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const unlock = new Date(capsule.unlock_date);
      const diff = unlock - now;
      
      if (diff <= 0) {
        setTimeLeft('Ready to open!');
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      if (days > 365) {
        const years = Math.floor(days / 365);
        setTimeLeft(`${years} year${years > 1 ? 's' : ''} left`);
      } else if (days > 30) {
        const months = Math.floor(days / 30);
        setTimeLeft(`${months} month${months > 1 ? 's' : ''} left`);
      } else if (days > 0) {
        setTimeLeft(`${days}d ${hours}h left`);
      } else {
        setTimeLeft(`${hours}h ${mins}m left`);
      }
    };
    
    updateTime();
    const interval = setInterval(updateTime, 60000);
    return () => clearInterval(interval);
  }, [capsule.unlock_date]);

  const typeIcons = {
    text: MessageCircle,
    video: Video,
    audio: Mic,
    image: ImageIcon,
  };
  const TypeIcon = typeIcons[capsule.type] || MessageCircle;

  return (
    <GlassCard 
      glow={!isLocked}
      onClick={() => !isLocked && onOpen?.(capsule)}
      className={`p-4 ${!isLocked ? 'cursor-pointer' : ''}`}
    >
      <div className="flex items-start gap-4">
        {/* Icon/Preview */}
        <div 
          className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${
            isLocked 
              ? 'bg-gradient-to-br from-gray-500/20 to-gray-600/20' 
              : 'bg-gradient-to-br from-indigo-500/30 to-purple-500/30'
          }`}
          style={!isLocked ? { animation: 'pulseGlow 3s ease-in-out infinite' } : {}}
        >
          {isLocked ? (
            <Lock className="w-6 h-6 text-gray-400" />
          ) : (
            <TypeIcon className="w-6 h-6 text-indigo-400" />
          )}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-white font-semibold text-sm truncate">{capsule.title}</span>
            {capsule.is_private && <Lock className="w-3 h-3 text-gray-500" />}
          </div>
          
          {/* Recipient */}
          {capsule.recipient && (
            <p className="text-xs text-purple-400 mb-1">
              To: {capsule.recipient.name || 'Future Me'}
            </p>
          )}
          
          {/* Time info */}
          <div className="flex items-center gap-2">
            <Clock className={`w-3.5 h-3.5 ${isLocked ? 'text-gray-500' : 'text-indigo-400'}`} />
            <span className={`text-xs ${isLocked ? 'text-gray-500' : 'text-indigo-400 font-medium'}`}>
              {timeLeft}
            </span>
          </div>
          
          {/* Unlock date */}
          <p className="text-[10px] text-gray-600 mt-1">
            Unlocks: {new Date(capsule.unlock_date).toLocaleDateString('en-US', { 
              year: 'numeric', month: 'short', day: 'numeric' 
            })}
          </p>
        </div>

        {/* Action */}
        {!isLocked && (
          <button className="p-2 rounded-full bg-indigo-500/20 text-indigo-400">
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>
    </GlassCard>
  );
};

// Sample data
const SAMPLE_CAPSULES = [
  {
    id: 'cap1',
    title: 'Letter to My Future Self',
    type: 'text',
    unlock_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // 1 year
    created_at: new Date().toISOString(),
    is_private: true,
    recipient: { name: 'Future Me' }
  },
  {
    id: 'cap2',
    title: 'Birthday Message for Mom',
    type: 'video',
    unlock_date: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(), // 90 days
    created_at: new Date().toISOString(),
    is_private: false,
    recipient: { name: 'Mom', picture: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100' }
  },
  {
    id: 'cap3',
    title: 'My Goals for 2030',
    type: 'audio',
    unlock_date: new Date('2030-01-01').toISOString(),
    created_at: new Date().toISOString(),
    is_private: true,
    recipient: { name: 'Future Me' }
  },
  {
    id: 'cap4',
    title: 'Wedding Anniversary',
    type: 'image',
    unlock_date: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString(), // 10 days - unlockable soon
    created_at: new Date().toISOString(),
    is_private: false,
    recipient: { name: 'My Love' }
  },
];

const SAMPLE_UNLOCKED = [
  {
    id: 'unlocked1',
    title: 'Remember This Day',
    type: 'video',
    content: 'A beautiful memory from last year...',
    thumbnail: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400',
    created_at: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
    unlocked_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
  }
];

// Main Component
export default function FutureEchoPage() {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('pending');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [capsules, setCapsules] = useState(SAMPLE_CAPSULES);
  const [unlockedCapsules, setUnlockedCapsules] = useState(SAMPLE_UNLOCKED);
  
  // Create capsule state
  const [newCapsule, setNewCapsule] = useState({
    title: '',
    type: 'text',
    content: '',
    unlock_date: '',
    recipient: 'self',
    is_private: true
  });

  // Inject keyframes
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = glassKeyframes;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const handleCreateCapsule = async () => {
    if (!newCapsule.title || !newCapsule.unlock_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Add to local state (would be API call in production)
    const capsule = {
      id: `cap_${Date.now()}`,
      ...newCapsule,
      created_at: new Date().toISOString(),
      recipient: newCapsule.recipient === 'self' ? { name: 'Future Me' } : { name: newCapsule.recipient }
    };
    
    setCapsules(prev => [capsule, ...prev]);
    setShowCreateModal(false);
    setNewCapsule({ title: '', type: 'text', content: '', unlock_date: '', recipient: 'self', is_private: true });
    toast.success('Time capsule created! It will unlock on the scheduled date.');
  };

  return (
    <div 
      className="min-h-screen text-white overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0f0f23, #1a1a3e, #0f0f23)',
      }}
      data-testid="future-echo-page"
    >
      {/* Floating Back Button */}
      <FloatingBackButton />

      {/* Floating orbs background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{
            background: 'radial-gradient(circle, #6366f1, transparent)',
            top: '10%',
            right: '-10%',
            animation: 'orbFloat 15s ease-in-out infinite',
          }}
        />
        <div 
          className="absolute w-80 h-80 rounded-full opacity-15 blur-3xl"
          style={{
            background: 'radial-gradient(circle, #8b5cf6, transparent)',
            bottom: '20%',
            left: '-5%',
            animation: 'orbFloat 20s ease-in-out infinite reverse',
          }}
        />
        <div 
          className="absolute w-64 h-64 rounded-full opacity-10 blur-3xl"
          style={{
            background: 'radial-gradient(circle, #06b6d4, transparent)',
            top: '50%',
            left: '30%',
            animation: 'orbFloat 25s ease-in-out infinite',
          }}
        />
      </div>

      {/* Header */}
      <header className="relative z-10 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
            Future Echo
          </h1>
          <p className="text-gray-500 text-sm">Send messages to the future</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="p-2 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10">
            <Bell className="w-5 h-5 text-gray-400" />
          </button>
          <div className="w-10 h-10 rounded-2xl overflow-hidden border-2 border-indigo-500/50">
            <img 
              src={user?.picture || 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100'} 
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative z-10 px-6 py-6">
        <GlassCard glow className="p-6">
          <div className="flex items-center gap-4">
            <div 
              className="w-20 h-20 rounded-3xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.3), rgba(139, 92, 246, 0.3))',
                animation: 'pulseGlow 3s ease-in-out infinite',
              }}
            >
              <Hourglass className="w-10 h-10 text-indigo-400" style={{ animation: 'floatSlow 4s ease-in-out infinite' }} />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-white mb-1">Create a Time Capsule</h2>
              <p className="text-gray-400 text-sm">
                Send messages, videos, or memories to yourself or loved ones in the future
              </p>
            </div>
          </div>
          <button 
            onClick={() => setShowCreateModal(true)}
            className="mt-4 w-full py-3 rounded-2xl font-semibold flex items-center justify-center gap-2"
            style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              boxShadow: '0 4px 20px rgba(99, 102, 241, 0.4)',
            }}
          >
            <Plus className="w-5 h-5" />
            Create New Capsule
          </button>
        </GlassCard>
      </div>

      {/* Quick Actions */}
      <div className="relative z-10 px-6 pb-4">
        <div className="grid grid-cols-4 gap-3">
          {[
            { icon: MessageCircle, label: 'Text', color: '#6366f1' },
            { icon: Video, label: 'Video', color: '#8b5cf6' },
            { icon: Mic, label: 'Voice', color: '#06b6d4' },
            { icon: ImageIcon, label: 'Photo', color: '#ec4899' },
          ].map(item => (
            <button
              key={item.label}
              onClick={() => { setNewCapsule(prev => ({ ...prev, type: item.label.toLowerCase() })); setShowCreateModal(true); }}
              className="flex flex-col items-center gap-2 p-3 rounded-2xl backdrop-blur-xl border border-white/10 hover:border-white/20 transition-all"
              style={{ background: 'rgba(255, 255, 255, 0.05)' }}
            >
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: `${item.color}20` }}
              >
                <item.icon className="w-5 h-5" style={{ color: item.color }} />
              </div>
              <span className="text-xs text-gray-400">{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="relative z-10 px-6 py-2">
        <div className="flex gap-2 p-1 rounded-2xl bg-white/5 backdrop-blur-xl">
          {[
            { id: 'pending', label: 'Pending', count: capsules.length },
            { id: 'unlocked', label: 'Unlocked', count: unlockedCapsules.length },
            { id: 'sent', label: 'Sent', count: 0 },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-indigo-500/30 to-purple-500/30 text-white border border-indigo-500/30'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                  activeTab === tab.id ? 'bg-indigo-500 text-white' : 'bg-white/10 text-gray-400'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Capsules List */}
      <div className="relative z-10 px-6 py-4 space-y-3 pb-32">
        {activeTab === 'pending' && (
          <>
            {capsules.map((capsule, idx) => (
              <motion.div
                key={capsule.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <TimeCapsuleCard capsule={capsule} />
              </motion.div>
            ))}
            
            {capsules.length === 0 && (
              <div className="text-center py-12">
                <Hourglass className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500">No pending time capsules</p>
                <button 
                  onClick={() => setShowCreateModal(true)}
                  className="mt-4 text-indigo-400 text-sm"
                >
                  Create your first capsule
                </button>
              </div>
            )}
          </>
        )}

        {activeTab === 'unlocked' && (
          <>
            {unlockedCapsules.map((capsule, idx) => (
              <motion.div
                key={capsule.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <GlassCard glow className="p-4 cursor-pointer">
                  <div className="flex gap-4">
                    {capsule.thumbnail && (
                      <div className="w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0">
                        <img src={capsule.thumbnail} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-semibold mb-1">{capsule.title}</h3>
                      <p className="text-gray-400 text-sm line-clamp-2">{capsule.content}</p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-indigo-400">
                        <Unlock className="w-3 h-3" />
                        <span>Opened {new Date(capsule.unlocked_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <Play className="w-8 h-8 text-indigo-400 flex-shrink-0" />
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </>
        )}

        {activeTab === 'sent' && (
          <div className="text-center py-12">
            <Send className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500">No capsules sent to others yet</p>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end justify-center"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="w-full max-w-lg rounded-t-3xl p-6 max-h-[85vh] overflow-y-auto"
              style={{
                background: 'linear-gradient(135deg, rgba(30, 30, 60, 0.98), rgba(20, 20, 45, 0.98))',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
              onClick={e => e.stopPropagation()}
            >
              <div className="w-12 h-1 bg-gray-600 rounded-full mx-auto mb-6" />
              
              <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <Hourglass className="w-6 h-6 text-indigo-400" />
                Create Time Capsule
              </h2>

              {/* Capsule Type */}
              <div className="mb-6">
                <label className="text-gray-400 text-sm mb-2 block">Capsule Type</label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { id: 'text', icon: MessageCircle, label: 'Text' },
                    { id: 'video', icon: Video, label: 'Video' },
                    { id: 'audio', icon: Mic, label: 'Audio' },
                    { id: 'image', icon: ImageIcon, label: 'Image' },
                  ].map(type => (
                    <button
                      key={type.id}
                      onClick={() => setNewCapsule(prev => ({ ...prev, type: type.id }))}
                      className={`flex flex-col items-center gap-1 p-3 rounded-xl border transition-all ${
                        newCapsule.type === type.id
                          ? 'bg-indigo-500/20 border-indigo-500/50'
                          : 'bg-white/5 border-white/10 hover:border-white/20'
                      }`}
                    >
                      <type.icon className={`w-5 h-5 ${newCapsule.type === type.id ? 'text-indigo-400' : 'text-gray-400'}`} />
                      <span className={`text-[10px] ${newCapsule.type === type.id ? 'text-indigo-400' : 'text-gray-500'}`}>{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div className="mb-4">
                <label className="text-gray-400 text-sm mb-2 block">Title *</label>
                <input
                  type="text"
                  value={newCapsule.title}
                  onChange={e => setNewCapsule(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Letter to my future self"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-indigo-500/50 outline-none"
                />
              </div>

              {/* Content */}
              <div className="mb-4">
                <label className="text-gray-400 text-sm mb-2 block">Message</label>
                <textarea
                  value={newCapsule.content}
                  onChange={e => setNewCapsule(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Write your message for the future..."
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-indigo-500/50 outline-none resize-none"
                />
              </div>

              {/* Unlock Date */}
              <div className="mb-4">
                <label className="text-gray-400 text-sm mb-2 block">Unlock Date *</label>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {[
                    { label: '1 Year', days: 365 },
                    { label: '5 Years', days: 365 * 5 },
                    { label: '10 Years', days: 365 * 10 },
                    { label: 'Custom', days: 0 },
                  ].map(preset => (
                    <button
                      key={preset.label}
                      onClick={() => {
                        if (preset.days > 0) {
                          const date = new Date(Date.now() + preset.days * 24 * 60 * 60 * 1000);
                          setNewCapsule(prev => ({ ...prev, unlock_date: date.toISOString().split('T')[0] }));
                        }
                      }}
                      className="py-2 rounded-xl text-xs bg-white/5 border border-white/10 text-gray-400 hover:border-indigo-500/50 hover:text-indigo-400 transition-colors"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
                <input
                  type="date"
                  value={newCapsule.unlock_date}
                  onChange={e => setNewCapsule(prev => ({ ...prev, unlock_date: e.target.value }))}
                  min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-indigo-500/50 outline-none"
                />
              </div>

              {/* Recipient */}
              <div className="mb-6">
                <label className="text-gray-400 text-sm mb-2 block">Send To</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setNewCapsule(prev => ({ ...prev, recipient: 'self' }))}
                    className={`flex-1 py-3 rounded-xl text-sm flex items-center justify-center gap-2 border transition-all ${
                      newCapsule.recipient === 'self'
                        ? 'bg-indigo-500/20 border-indigo-500/50 text-indigo-400'
                        : 'bg-white/5 border-white/10 text-gray-400'
                    }`}
                  >
                    <Rocket className="w-4 h-4" />
                    Future Me
                  </button>
                  <button
                    onClick={() => setNewCapsule(prev => ({ ...prev, recipient: '' }))}
                    className={`flex-1 py-3 rounded-xl text-sm flex items-center justify-center gap-2 border transition-all ${
                      newCapsule.recipient !== 'self'
                        ? 'bg-indigo-500/20 border-indigo-500/50 text-indigo-400'
                        : 'bg-white/5 border-white/10 text-gray-400'
                    }`}
                  >
                    <Users className="w-4 h-4" />
                    Someone Else
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 py-3 rounded-xl bg-white/5 text-gray-400 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateCapsule}
                  className="flex-1 py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
                  style={{
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    boxShadow: '0 4px 20px rgba(99, 102, 241, 0.4)',
                  }}
                >
                  <Send className="w-4 h-4" />
                  Create Capsule
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
